// tabs/EditTab.tsx
import { useEffect, useState } from 'react';
import { Stack, Textarea, Switch, Divider, Group, Button } from '@mantine/core';
import { useLeaderService, useLeaderTypeService, LeaderType, LeaderModelView, Leader } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';
import { Select as CCMSSelect, Checkbox } from '@/components';
import { LEADER_BIO_MAX_LENGTH } from '../../constants';

interface EditTabProps {
  leader: LeaderModelView | null;
  onSaved?: () => void;
}

export default function EditTab({ leader, onSaved }: EditTabProps) {
  const Leaders = useLeaderService();
  const LeaderTypes = useLeaderTypeService();

  const [allTypes, setAllTypes] = useState<LeaderType[]>([]);
  const [editForm, setEditForm] = useState<Partial<Leader>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    LeaderTypes.getAll().then(setAllTypes).catch(console.error);
  }, [LeaderTypes]);

  useEffect(() => {
    if (!leader) return;
    setEditForm({
      userId: leader.userId,
      memberID: leader.memberID,
      typeID: leader.typeID,
      bio: leader.bio ?? '',
      supportsVirtual: leader.supportsVirtual,
      supportsInPerson: leader.supportsInPerson,
      isActive: leader.isActive,
      professional: leader.professional,
      createdDateTime: leader.createdDateTime ?? 0,
      modifiedDateTime: leader.modifiedDateTime ?? 0,
    });
  }, [leader]);

  const handleSaveEdit = async () => {
    if (!leader) return;
    setSaving(true);
    try {
      const payload: Leader = {
        userId: leader.userId,
        memberID: leader.memberID,
        typeID: editForm.typeID ?? leader.typeID,
        bio: editForm.bio ?? leader.bio,
        supportsVirtual: editForm.supportsVirtual ?? leader.supportsVirtual,
        supportsInPerson: editForm.supportsInPerson ?? leader.supportsInPerson,
        isActive: editForm.isActive ?? leader.isActive,
        professional: editForm.professional ?? leader.professional,
        createdDateTime: leader.createdDateTime ?? 0,
        modifiedDateTime: leader.modifiedDateTime ?? 0,
      };
      await Leaders.update(leader.userId, payload);
      onSaved?.();
      toast.success('Updated', 'Leader updated successfully.');
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to update leader.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md">
      <CCMSSelect
        label="Leader Type"
        placeholder="Select a type"
        data={allTypes.map((t) => ({ value: String(t.typeID), label: t.name ?? String(t.typeID) }))}
        value={String(editForm.typeID ?? leader?.typeID ?? 1)}
        onChange={(val) => setEditForm({ ...editForm, typeID: Number(val) || 1 })}
      />

      <Textarea
        label="Biography"
        placeholder="Leader biography"
        value={editForm.bio ?? ''}
        onChange={(e) =>
          setEditForm({ ...editForm, bio: e.currentTarget.value.slice(0, LEADER_BIO_MAX_LENGTH) })
        }
        maxLength={LEADER_BIO_MAX_LENGTH}
        description={`${(editForm.bio ?? '').length}/${LEADER_BIO_MAX_LENGTH} characters`}
        minRows={3}
      />

      <Switch
        label="Supports Virtual"
        checked={editForm.supportsVirtual ?? true}
        onChange={(e) => setEditForm({ ...editForm, supportsVirtual: e.currentTarget.checked })}
      />

      <Switch
        label="Supports In Person"
        checked={editForm.supportsInPerson ?? true}
        onChange={(e) => setEditForm({ ...editForm, supportsInPerson: e.currentTarget.checked })}
      />

      <Switch
        label="Active ( Available to serve )"
        checked={editForm.isActive ?? true}
        onChange={(e) => setEditForm({ ...editForm, isActive: e.currentTarget.checked })}
      />

      <Checkbox
        label="Professional"
        checked={editForm.professional ?? false}
        onChange={(e) => setEditForm({ ...editForm, professional: e.currentTarget.checked })}
      />

      <Divider my="sm" />

      <Group justify="flex-end">
        <Button loading={saving} onClick={handleSaveEdit}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}